import React, { useEffect, useRef, useState } from 'react';

import {
  PolarAngleAxis,
  PolarGrid,
  Radar,
  RadarChart,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from 'recharts';

// ─── Types ─────────────────────────────────────────────────────────────────

type Metric = { label: string; score: number; max: number };
type Category = { key: string; label: string; color: string; metrics: Metric[] };

type MeetingData = {
  overallScore: number;
  aiData: {
    summary: string[];
    insights: string[];
    suggestions: string[];
    justification: string[];
    categories: Category[];
  };
  // talkRatio: { agent: number; customer: number };
  salesRep?: string;
  company?: string;
  meetingType?: string;
};

interface FeedbackReportModalProps {
  open: boolean;
  onClose: () => void;
  meeting: MeetingData;
}

// ─── Helpers ───────────────────────────────────────────────────────────────

const clamp = (value: number, min = 0, max = 100) => Math.min(Math.max(value, min), max);

const categoryAvg = (metrics: Metric[]) => {
  if (!metrics?.length) return 0;
  const total = metrics.reduce((s, m) => s + Math.max(0, m.score), 0);
  const maxTotal = metrics.reduce((s, m) => s + Math.max(1, m.max), 0);
  return maxTotal > 0 ? clamp(Math.round((total / maxTotal) * 100), 0, 100) : 0;
};

// ─── Sub-components ────────────────────────────────────────────────────────

const ScoreBar = ({
  label,
  score,
  max,
  color,
}: {
  label: string;
  score: number;
  max: number;
  color: string;
}) => {
  const safeMax = Math.max(1, max);
  const pct = clamp((Math.max(0, score) / safeMax) * 100, 0, 100);
  return (
    <div className="flex items-center gap-3">
      <span className="w-40 flex-shrink-0 text-[14px] text-[#ffffffe0] truncate" title={label}>
        {label}
      </span>
      <div className="flex-1 h-[5px] rounded-full bg-white/[0.05]">
        <div
          className="h-full rounded-full transition-all duration-700"
          style={{ width: `${pct}%`, backgroundColor: color }}
        />
      </div>
      <span className="w-10 flex-shrink-0 text-right text-[14px] font-semibold text-[#ffffffe0]">
        {score}/{max}
      </span>
    </div>
  );
};

const CollapsibleSection = ({
  icon,
  title,
  points,
  accentColor,
  defaultOpen = false,
}: {
  icon: React.ReactNode;
  title: string;
  points: string[];
  accentColor: string;
  defaultOpen?: boolean;
}) => {
  const [open, setOpen] = useState(defaultOpen);
  if (!points?.length) return null;
  return (
    <div className="rounded-xl border border-white/[0.06] overflow-hidden">
      <button
        onClick={() => setOpen((p) => !p)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/[0.03] transition-colors"
      >
        <div className="flex items-center gap-2">
          <span style={{ color: accentColor }}>{icon}</span>
          <span className="text-[14px] font-semibold uppercase tracking-widest text-[#ffffffe0]">
            {title}
          </span>
          <span
            className="rounded-full px-2 py-0.5 text-[12px]"
            style={{ backgroundColor: `${accentColor}20`, color: accentColor }}
          >
            {points.length}
          </span>
        </div>
        <svg
          className={`h-4 w-4 text-whitesmoke-600 transition-transform duration-300 ${open ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div className="px-4 pb-4 mt-1 space-y-2.5">
          {points.map((p, i) => (
            <div key={i} className="flex gap-2.5">
              <span
                className="mt-1.5 flex-shrink-0 w-1.5 h-1.5 rounded-full"
                style={{ backgroundColor: accentColor }}
              />
              <p className="text-[14px] leading-6 text-[#ffffffe0]">{p}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const OverallRing = ({ score }: { score: number }) => {
  const safeScore = clamp(score, 0, 100);
  const r = 54;
  const circ = 2 * Math.PI * r;
  const dash = (safeScore / 100) * circ;
  const color = safeScore >= 70 ? '#34d399' : safeScore >= 40 ? '#f59e0b' : '#f87171';
  return (
    <div className="relative flex-shrink-0 w-32 h-32 flex items-center justify-center">
      <svg width="128" height="128" viewBox="0 0 128 128">
        <circle cx="64" cy="64" r={r} fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="8" />
        <circle
          cx="64"
          cy="64"
          r={r}
          fill="none"
          stroke={color}
          strokeWidth="8"
          strokeDasharray={`${dash} ${circ}`}
          strokeLinecap="round"
          transform="rotate(-90 64 64)"
          style={{ filter: `drop-shadow(0 0 8px ${color}50)` }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-3xl font-bold" style={{ color }}>
          {safeScore}%
        </span>
        <span className="text-[10px] uppercase tracking-widest text-gray-600 mt-0.5">Score</span>
      </div>
    </div>
  );
};

// ─── Modal ─────────────────────────────────────────────────────────────────

const TABS = ['Overview', 'Scores', 'Insights'] as const;
type Tab = (typeof TABS)[number];

const FeedbackReportModal: React.FC<FeedbackReportModalProps> = ({ open, onClose, meeting }) => {
  const [activeTab, setActiveTab] = useState<Tab>('Overview');
  const [activeCat, setActiveCat] = useState(0);
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  // Reset tab when modal opens
  useEffect(() => {
    if (open) {
      setActiveTab('Overview');
      setActiveCat(0);
    }
  }, [open]);

  if (!open) return null;

  const { overallScore, aiData } = meeting;
  const categories: Category[] = aiData.categories || [];

  const radarData = categories.map((cat) => ({
    subject: cat.label.length > 10 ? cat.label.split(' ')[0] : cat.label,
    value: categoryAvg(cat.metrics),
    fullMark: 100,
  }));

  const cat = categories[activeCat] || null;

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 pt-24"
      style={{ backgroundColor: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(8px)' }}
      onClick={(e) => {
        if (e.target === overlayRef.current) onClose();
      }}
    >
      <div
        className="relative w-full max-w-3xl max-h-[80vh] flex flex-col rounded-2xl border border-white/[0.08] bg-[#0e111fde] shadow-2xl overflow-hidden"
        style={{ animation: 'modalIn 0.2s ease' }}
      >
        <style>{`
          @keyframes modalIn {
            from { opacity: 0; transform: scale(0.97) translateY(10px); }
            to   { opacity: 1; transform: scale(1) translateY(0); }
          }
        `}</style>

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/[0.06] flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-[#B8F890]/10 flex items-center justify-center border border-[#B8F890]/20">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#B8F890"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M9 11l3 3L22 4" />
                <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" />
              </svg>
            </div>
            <div>
              <h2 className="text-sm font-semibold text-white">Feedback Report</h2>
              <div className="flex items-center gap-2 mt-0.5">
                {meeting.salesRep && (
                  <span className="text-[16px] text-whitesmoke-500">{meeting.salesRep}</span>
                )}
                {meeting.company && (
                  <>
                    <span className="text-white-700">·</span>
                    <span className="text-[16px] text-[#ffffffe0]">{meeting.company}</span>
                  </>
                )}
                {meeting.meetingType && (
                  <span className="rounded-full bg-white/[0.05] px-2 py-1 text-[12px] uppercase tracking-wider text-[#ffffffe0]">
                    {meeting.meetingType}
                  </span>
                )}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full flex items-center justify-center text-whitesmoke-600 hover:bg-white/[0.06] hover:text-[#ffffffe0] transition-colors"
          >
            <svg
              width="16"
              height="16"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 px-6 py-4 flex-shrink-0">
          {TABS.map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-1.5 rounded-full text-[12px] font-semibold uppercase tracking-widest transition-all duration-200 ${
                activeTab === tab
                  ? 'bg-[#B8F890]/15 text-[#B8F890] border border-[#B8F890]/30'
                  : 'text-[#ffffffe0] hover:text-gray-400'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Scrollable Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {/* ── OVERVIEW ── */}
          {activeTab === 'Overview' && (
            <>
              <div className="flex flex-col sm:flex-row gap-5 items-center sm:items-start">
                <div className="flex flex-col items-center gap-2">
                  <OverallRing score={overallScore} />
                  <span className="text-[10px] uppercase tracking-widest text-[#ffffffe0]">
                    Overall Score
                  </span>
                </div>
                {radarData.length > 0 ? (
                  <div className="flex-1 w-full h-52">
                    <ResponsiveContainer width="100%" height="100%">
                      <RadarChart data={radarData} outerRadius="75%">
                        <PolarGrid stroke="rgba(255,255,255,0.06)" />
                        <PolarAngleAxis
                          dataKey="subject"
                          tick={{ fill: '#6b7280', fontSize: 12 }}
                        />
                        <RechartsTooltip
                          contentStyle={{
                            backgroundColor: '#1C1A27',
                            border: '1px solid rgba(255,255,255,0.1)',
                            borderRadius: 8,
                            fontSize: 11,
                            color: '#fff',
                          }}
                          formatter={(value) => [`${Number(value)}%`, 'Score']}
                        />
                        <Radar
                          dataKey="value"
                          stroke="#B8F890"
                          fill="#B8F890"
                          fillOpacity={0.12}
                          strokeWidth={1.5}
                        />
                      </RadarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="flex-1 flex items-center justify-center h-52 rounded-xl border border-white/[0.05] bg-white/[0.02]">
                    <p className="text-sm text-gray-600">No radar data available</p>
                  </div>
                )}
              </div>

              {/* Category pills */}
              {categories.length > 0 && (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {categories.map((c) => {
                    const avg = categoryAvg(c.metrics);
                    return (
                      <div
                        key={c.key}
                        className="rounded-xl border border-white/[0.05] bg-white/[0.02] p-3 text-center"
                      >
                        <div className="text-lg font-bold" style={{ color: c.color }}>
                          {avg}%
                        </div>
                        <div className="text-[11px] uppercase tracking-widest text-[#ffffffe0] mt-0.5 leading-tight">
                          {c.label}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Collapsible sections */}
              <div className="space-y-2">
                <CollapsibleSection
                  icon={
                    <svg
                      width="14"
                      height="14"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                  }
                  title="Summary"
                  points={aiData.summary}
                  accentColor="#a78bfa"
                  defaultOpen
                />
                <CollapsibleSection
                  icon={
                    <svg
                      width="16"
                      height="16"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                      />
                    </svg>
                  }
                  title="Justification"
                  points={aiData.justification}
                  accentColor="#38bdf8"
                />
                <CollapsibleSection
                  icon={
                    <svg
                      width="16"
                      height="16"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M13 10V3L4 14h7v7l9-11h-7z"
                      />
                    </svg>
                  }
                  title="Suggestions"
                  points={aiData.suggestions}
                  accentColor="#B8F890"
                />
              </div>
            </>
          )}

          {/* ── SCORES ── */}
          {activeTab === 'Scores' &&
            (categories.length > 0 ? (
              <div className="flex gap-4">
                {/* Sidebar */}
                <div className="flex-shrink-0 space-y-1 w-36">
                  {categories.map((c, i) => {
                    const avg = categoryAvg(c.metrics);
                    return (
                      <button
                        key={c.key}
                        onClick={() => setActiveCat(i)}
                        className={`w-full flex items-center justify-between rounded-xl px-3 py-2 text-left transition-all duration-200 ${
                          activeCat === i
                            ? 'bg-white/[0.07] border border-white/[0.1]'
                            : 'hover:bg-white/[0.03] border border-transparent'
                        }`}
                      >
                        <div>
                          <div
                            className="text-[10px] uppercase tracking-widest font-semibold"
                            style={{ color: activeCat === i ? c.color : '#6b7280' }}
                          >
                            {c.label.split(' ')[0]}
                          </div>
                          <div className="text-sm font-bold text-white">{avg}%</div>
                        </div>
                        <div
                          className="w-1 h-8 rounded-full"
                          style={{ backgroundColor: activeCat === i ? c.color : 'transparent' }}
                        />
                      </button>
                    );
                  })}
                </div>

                {/* Score bars */}
                {cat && (
                  <div className="flex-1 rounded-xl border border-white/[0.06] bg-white/[0.02] p-5">
                    <div className="flex items-center justify-between mb-5">
                      <span
                        className="text-xs font-bold uppercase tracking-widest"
                        style={{ color: cat.color }}
                      >
                        {cat.label}
                      </span>
                      <span className="text-xl font-bold text-white">
                        {categoryAvg(cat.metrics)}%
                      </span>
                    </div>
                    {/* Donut */}
                    <div className="flex justify-center mb-5">
                      <div className="relative w-24 h-24">
                        <svg width="96" height="96" viewBox="0 0 96 96">
                          <circle
                            cx="48"
                            cy="48"
                            r="38"
                            fill="none"
                            stroke="rgba(255,255,255,0.05)"
                            strokeWidth="8"
                          />
                          <circle
                            cx="48"
                            cy="48"
                            r="38"
                            fill="none"
                            stroke={cat.color}
                            strokeWidth="8"
                            strokeDasharray={`${(categoryAvg(cat.metrics) / 100) * 2 * Math.PI * 38} ${2 * Math.PI * 38}`}
                            strokeLinecap="round"
                            transform="rotate(-90 48 48)"
                            style={{ opacity: 0.85 }}
                          />
                        </svg>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-base font-bold" style={{ color: cat.color }}>
                            {categoryAvg(cat.metrics)}%
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="space-y-3">
                      {cat.metrics.map((m) => (
                        <ScoreBar
                          key={m.label}
                          label={m.label}
                          score={m.score}
                          max={m.max}
                          color={cat.color}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-center h-48 rounded-xl border border-white/[0.06] bg-white/[0.02]">
                <p className="text-gray-600 text-sm">No score breakdown available</p>
              </div>
            ))}

          {/* ── INSIGHTS ── */}
          {activeTab === 'Insights' && (
            <div className="space-y-4">
              {/* Key Insights */}
              {aiData.insights.length > 0 && (
                <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <svg
                      width="16"
                      height="16"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="#f59e0b"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                      />
                    </svg>
                    <span className="text-[14px] uppercase tracking-widest font-semibold text-[#ffffffe0]">
                      Key Insights
                    </span>
                  </div>
                  <div className="space-y-2">
                    {aiData.insights.map((p, i) => (
                      <div key={i} className="flex gap-2.5">
                        <span className="mt-1.5 flex-shrink-0 w-2 h-2 rounded-full bg-amber-400" />
                        <p className="text-[14px] leading-6 text-[#ffffffe0]">{p}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Strengths & Gaps */}
              {categories.length > 0 && (
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-4">
                    <div className="flex items-center gap-1.5 mb-3">
                      <svg
                        width="16"
                        height="16"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="#34d399"
                        strokeWidth={2.5}
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-[14px] uppercase tracking-widest font-semibold text-emerald-400">
                        Strengths
                      </span>
                    </div>
                    <div className="space-y-1.5">
                      {categories
                        .filter((c) => categoryAvg(c.metrics) >= 40)
                        .map((c) => (
                          <div key={c.key} className="flex items-center justify-between">
                            <span className="text-[14px] text-[#ffffffe0]">{c.label}</span>
                            <span className="text-[14px] font-semibold" style={{ color: c.color }}>
                              {categoryAvg(c.metrics)}%
                            </span>
                          </div>
                        ))}
                      {categories.filter((c) => categoryAvg(c.metrics) >= 40).length === 0 && (
                        <p className="text-[12px] text-[#ffffffe0]">No strong areas yet</p>
                      )}
                    </div>
                  </div>
                  <div className="rounded-xl border border-rose-500/20 bg-rose-500/5 p-4">
                    <div className="flex items-center gap-1.5 mb-3">
                      <svg
                        width="16"
                        height="16"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="#f87171"
                        strokeWidth={2.5}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M12 9v2m0 4h.01M12 5a7 7 0 100 14A7 7 0 0012 5z"
                        />
                      </svg>
                      <span className="text-[14px] uppercase tracking-widest font-semibold text-rose-400">
                        Gaps
                      </span>
                    </div>
                    <div className="space-y-1.5">
                      {categories
                        .filter((c) => categoryAvg(c.metrics) < 40)
                        .map((c) => (
                          <div key={c.key} className="flex items-center justify-between">
                            <span className="text-[14px] text-[#ffffffe0]">{c.label}</span>
                            <span className="text-[14px] font-semibold text-rose-400">
                              {categoryAvg(c.metrics)}%
                            </span>
                          </div>
                        ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Suggestions */}
              {aiData.suggestions.length > 0 && (
                <div className="rounded-xl border border-[#B8F890]/20 bg-[#B8F890]/5 p-4">
                  <div className="flex items-center gap-1.5 mb-3">
                    <svg
                      width="18"
                      height="18"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="#B8F890"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M13 10V3L4 14h7v7l9-11h-7z"
                      />
                    </svg>
                    <span className="text-[14px] uppercase tracking-widest font-semibold text-[#B8F890]">
                      Suggestions
                    </span>
                  </div>
                  <div className="space-y-2">
                    {aiData.suggestions.map((p, i) => (
                      <div key={i} className="flex gap-2.5">
                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-[#B8F890]/20 text-[#B8F890] text-[10px] flex items-center justify-center font-bold mt-0.5">
                          {i + 1}
                        </span>
                        <p className="text-[14px] leading-5 text-[#ffffffe0]">{p}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {aiData.insights.length === 0 &&
                aiData.suggestions.length === 0 &&
                categories.length === 0 && (
                  <div className="flex items-center justify-center h-48 rounded-xl border border-white/[0.06] bg-white/[0.02]">
                    <p className="text-gray-600 text-sm">No insights available for this session</p>
                  </div>
                )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 flex items-center justify-between px-6 py-3 border-t border-white/[0.05]">
          <span className="text-[10px] uppercase tracking-widest text-[#ffffffe0]">
            AI Session Analysis
          </span>
          <button
            onClick={onClose}
            className="rounded-full border border-white/[0.1] px-4 py-1.5 text-[11px] font-semibold text-gray-400 hover:bg-white/[0.05] hover:text-gray-200 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default FeedbackReportModal;
